import { useState, type JSX } from 'react';
import { z } from 'zod';
import {
  disposalDecisionSchema,
  generateUuidV7,
  privacyCaseSchema,
  privacyExportPageSchema,
  privacyExportSectionSchema,
  privacyRequestSchema,
  type BotScope,
} from '@cryptology-assist/domain';
import { botPath, request, type Session } from './api';
import { ErrorNotice, Field, useMutation } from './components';
import { useRemote } from './hooks';
import { dateLabel, statusLabel, type Copy, type Language } from './copy';

const casesSchema = z.object({
  cases: z.array(privacyCaseSchema),
  nextCursor: z.string().nullable(),
});
const caseSchema = z.object({ case: privacyCaseSchema });
const savedSchema = z.object({ saved: z.literal(true) });
const disposalSchema = z.object({
  complete: z.boolean(),
  processedObjects: z.number(),
  case: privacyCaseSchema,
});
const holdsSchema = z.object({
  holds: z.array(
    z.object({
      id: z.string(),
      basis_reference: z.string(),
      released_at: z.string().nullable(),
      release_reference: z.string().nullable(),
    })
  ),
  nextCursor: z.string().nullable(),
});
const peruTime = (value: FormDataEntryValue | null) =>
  `${String(value)}:00-05:00`;
const inputValue = (form: FormData, key: string) =>
  String(form.get(key) ?? '').trim();

function downloadPage(
  value: z.infer<typeof privacyExportPageSchema>,
  caseId: string
) {
  const blob = new Blob([JSON.stringify(value, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `privacy-${caseId}-${value.section}-${value.records[0]?.id ?? 'empty'}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function Privacy({
  scope,
  session,
  copy,
  language,
}: {
  scope: BotScope;
  session: Session;
  copy: Copy;
  language: Language;
}): JSX.Element {
  const [before, setBefore] = useState<string | null>(null);
  const [selected, setSelected] = useState('');
  const cases = useRemote(
    botPath(scope, `privacy${before ? `?before=${before}` : ''}`),
    casesSchema
  );
  const mutation = useMutation();
  return (
    <section className="settings-section">
      <h1>{copy.privacy}</h1>
      <p>{copy.privacyScope}</p>
      <ErrorNotice
        copy={copy}
        error={cases.error ?? mutation.error}
        onRetry={cases.reload}
      />
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          void mutation.run(async () => {
            const result = await request(
              botPath(scope, 'privacy'),
              caseSchema,
              {
                csrf: session.csrf,
                body: privacyRequestSchema.parse({
                  conversationId: inputValue(form, 'conversationId'),
                  reason: inputValue(form, 'reason'),
                  authorityReference: inputValue(form, 'authorityReference'),
                }),
              }
            );
            setSelected(result.case.id);
            cases.reload();
          });
        }}
      >
        <h2>{copy.privacyBlock}</h2>
        <Field label={copy.conversationId}>
          <input name="conversationId" required />
        </Field>
        <Field label={copy.privacyReason}>
          <select name="reason" required>
            {privacyRequestSchema.shape.reason.options.map((reason) => (
              <option key={reason} value={reason}>
                {copy[reason]}
              </option>
            ))}
          </select>
        </Field>
        <Field label={copy.authorityReference}>
          <textarea name="authorityReference" required maxLength={1000} />
        </Field>
        <button disabled={mutation.busy}>{copy.privacyBlock}</button>
      </form>
      {cases.loading && <p>{copy.loading}</p>}
      {cases.data && (
        <>
          <ul>
            {cases.data.cases.map((item) => (
              <li key={item.id}>
                <button onClick={() => setSelected(item.id)}>
                  {item.conversationId} · {statusLabel(copy, item.state)}
                </button>
                <span> · {dateLabel(item.createdAt, language)}</span>
              </li>
            ))}
          </ul>
          {cases.data.cases.length === 0 && <p>{copy.privacyNoCases}</p>}
          {before && (
            <button onClick={() => setBefore(null)}>{copy.firstPage}</button>
          )}
          {cases.data.nextCursor && (
            <button onClick={() => setBefore(cases.data!.nextCursor)}>
              {copy.nextPage}
            </button>
          )}
        </>
      )}
      {selected && (
        <PrivacyCase
          key={`${scope.companyId}/${scope.botId}/${selected}`}
          {...{ scope, session, copy, language }}
          id={selected}
          onChange={cases.reload}
        />
      )}
    </section>
  );
}

function PrivacyCase({
  scope,
  session,
  copy,
  language,
  id,
  onChange,
}: {
  scope: BotScope;
  session: Session;
  copy: Copy;
  language: Language;
  id: string;
  onChange: () => void;
}): JSX.Element {
  const path = botPath(scope, `privacy/${id}`);
  const current = useRemote(path, caseSchema);
  const [holdCursor, setHoldCursor] = useState<string | null>(null);
  const holds = useRemote(
    `${path}/holds${holdCursor ? `?before=${holdCursor}` : ''}`,
    holdsSchema
  );
  const [section, setSection] =
    useState<z.infer<typeof privacyExportSectionSchema>>('messages');
  const [page, setPage] = useState<z.infer<
    typeof privacyExportPageSchema
  > | null>(null);
  const mutation = useMutation();
  const [tax, setTax] = useState(false);
  const [contractEnded, setContractEnded] = useState(false);
  const [downstream, setDownstream] = useState<
    { recipient: string; instructionReference: string }[]
  >([]);
  const reload = () => {
    current.reload();
    holds.reload();
    onChange();
  };
  const editable =
    current.data && ['blocked', 'scheduled'].includes(current.data.case.state);
  return (
    <section className="form-section">
      <h2>{copy.privacyCase}</h2>
      <ErrorNotice
        copy={copy}
        error={current.error ?? holds.error ?? mutation.error}
        onRetry={reload}
      />
      {current.loading && <p>{copy.loading}</p>}
      {current.data && (
        <>
          <p>
            {current.data.case.conversationId} ·{' '}
            {statusLabel(copy, current.data.case.state)}
          </p>
          <p>{current.data.case.authorityReference}</p>
          {current.data.case.deleteAfter && (
            <p>
              {copy.privacyDeleteAfter}:{' '}
              {dateLabel(current.data.case.deleteAfter, language)}
            </p>
          )}
          {current.data.case.decision && (
            <details>
              <summary>{copy.privacyDecision}</summary>
              <dl>
                <dt>{copy.privacyDecisionBasis}</dt>
                <dd>{current.data.case.decision.basisReference}</dd>
                <dt>{copy.privacyPurposeEnded}</dt>
                <dd>
                  {dateLabel(
                    current.data.case.decision.purposeEndedAt,
                    language
                  )}
                </dd>
                <dt>{copy.privacyRetainUntil}</dt>
                <dd>
                  {dateLabel(current.data.case.decision.retainUntil, language)}
                </dd>
                <dt>{copy.privacyFinancialClass}</dt>
                <dd>
                  {copy[current.data.case.decision.financialClassification]}
                </dd>
                {current.data.case.decision.tax && (
                  <>
                    <dt>{copy.privacyFilingDeadline}</dt>
                    <dd>{current.data.case.decision.tax.filingDeadline}</dd>
                    <dt>{copy.privacyPrescriptionEnd}</dt>
                    <dd>
                      {dateLabel(
                        current.data.case.decision.tax.prescriptionEndsAt,
                        language
                      )}
                    </dd>
                    <dt>{copy.privacyTaxDetermination}</dt>
                    <dd>
                      {current.data.case.decision.tax.determinationReference}
                    </dd>
                  </>
                )}
                {current.data.case.decision.processorContractEndedAt && (
                  <>
                    <dt>{copy.privacyContractDate}</dt>
                    <dd>
                      {dateLabel(
                        current.data.case.decision.processorContractEndedAt,
                        language
                      )}
                    </dd>
                    <dt>{copy.privacyControllerReturn}</dt>
                    <dd>
                      {current.data.case.decision.controllerReturnReference}
                    </dd>
                  </>
                )}
                {current.data.case.decision.statutoryExceptionReference && (
                  <>
                    <dt>{copy.privacyException}</dt>
                    <dd>
                      {current.data.case.decision.statutoryExceptionReference}
                    </dd>
                  </>
                )}
              </dl>
              <ul>
                {current.data.case.decision.downstreamInstructions.map(
                  (item, index) => (
                    <li key={index}>
                      {item.recipient}: {item.instructionReference}
                    </li>
                  )
                )}
              </ul>
            </details>
          )}
          {current.data.case.completion && (
            <div className="notice">
              <p>{copy.privacyCompleted}</p>
              <p>{copy.privacyRetained}</p>
              <p>
                {copy.privacyDeletedObjects}:{' '}
                {current.data.case.completion.deletedObjects}
              </p>
              <p>
                {copy.privacyRetainedUsage}:{' '}
                {current.data.case.completion.retainedUsageEvents}
              </p>
              <p>
                {copy.privacyRetainedWindows}:{' '}
                {current.data.case.completion.retainedInteractionWindows}
              </p>
              <p>
                {current.data.case.completion.customerProfileCleared
                  ? copy.privacyProfileCleared
                  : copy.privacySharedProfile}
              </p>
            </div>
          )}
          {editable && (
            <>
              <h3>{copy.privacyExport}</h3>
              <p>{copy.privacyExportHelp}</p>
              <Field label={copy.privacyExportSection}>
                <select
                  value={section}
                  onChange={(event) => {
                    setSection(
                      privacyExportSectionSchema.parse(event.target.value)
                    );
                    setPage(null);
                  }}
                >
                  {privacyExportSectionSchema.options.map((item) => (
                    <option key={item} value={item}>
                      {copy[`export_${item}`]}
                    </option>
                  ))}
                </select>
              </Field>
              <button
                disabled={mutation.busy}
                onClick={() =>
                  void mutation.run(async () => {
                    const result = await request(
                      `${path}/export/${section}`,
                      privacyExportPageSchema
                    );
                    setPage(result);
                    downloadPage(result, id);
                  })
                }
              >
                {copy.privacyDownloadFirst}
              </button>
              {page?.nextCursor && (
                <button
                  disabled={mutation.busy}
                  onClick={() =>
                    void mutation.run(async () => {
                      const result = await request(
                        `${path}/export/${section}?after=${encodeURIComponent(page.nextCursor!)}`,
                        privacyExportPageSchema
                      );
                      setPage(result);
                      downloadPage(result, id);
                    })
                  }
                >
                  {copy.privacyDownloadNext}
                </button>
              )}
              {page && !page.nextCursor && <p>{copy.privacyExportEnd}</p>}
              {page?.section === 'conversation_attachments' && (
                <ul>
                  {page.records.map((item) => (
                    <li key={item.id}>
                      <a href={`${path}/attachments/${item.id}`} download>
                        {String(item.payload.filename)}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          <h3>{copy.privacyHolds}</h3>
          {holds.data?.holds.map((hold) => (
            <div key={hold.id}>
              <p>{hold.basis_reference}</p>
              {hold.released_at ? (
                <p>
                  {copy.privacyReleased}:{' '}
                  {dateLabel(hold.released_at, language)} ·{' '}
                  {hold.release_reference}
                </p>
              ) : (
                editable && (
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      const form = new FormData(event.currentTarget);
                      void mutation.run(async () => {
                        await request(
                          `${path}/holds/${hold.id}/release`,
                          savedSchema,
                          {
                            csrf: session.csrf,
                            body: {
                              releaseReference: inputValue(
                                form,
                                'releaseReference'
                              ),
                            },
                          }
                        );
                        reload();
                      });
                    }}
                  >
                    <Field label={copy.privacyReleaseReference}>
                      <input
                        required
                        maxLength={1000}
                        name="releaseReference"
                      />
                    </Field>
                    <button disabled={mutation.busy}>
                      {copy.privacyReleaseHold}
                    </button>
                  </form>
                )
              )}
            </div>
          ))}
          {holdCursor && (
            <button onClick={() => setHoldCursor(null)}>
              {copy.firstPage}
            </button>
          )}
          {holds.data?.nextCursor && (
            <button onClick={() => setHoldCursor(holds.data!.nextCursor)}>
              {copy.nextPage}
            </button>
          )}
          {editable && (
            <>
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  const form = new FormData(event.currentTarget);
                  void mutation.run(async () => {
                    await request(`${path}/holds`, savedSchema, {
                      csrf: session.csrf,
                      body: {
                        id: generateUuidV7(),
                        basisReference: inputValue(form, 'basisReference'),
                      },
                    });
                    reload();
                  });
                }}
              >
                <Field label={copy.privacyHoldBasis}>
                  <textarea name="basisReference" required maxLength={1000} />
                </Field>
                <button disabled={mutation.busy}>{copy.privacyAddHold}</button>
              </form>
              <h3>{copy.privacyDecision}</h3>
              <p>{copy.privacyDecisionHelp}</p>
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  const form = new FormData(event.currentTarget);
                  void mutation.run(async () => {
                    const financialClassification = inputValue(
                      form,
                      'financialClassification'
                    );
                    const body = disposalDecisionSchema.parse({
                      purposeEndedAt: peruTime(form.get('purposeEndedAt')),
                      retainUntil: peruTime(form.get('retainUntil')),
                      basisReference: inputValue(form, 'basisReference'),
                      financialClassification,
                      tax:
                        financialClassification === 'tax_evidence'
                          ? {
                              filingDeadline: inputValue(
                                form,
                                'filingDeadline'
                              ),
                              prescriptionEndsAt: peruTime(
                                form.get('prescriptionEndsAt')
                              ),
                              determinationReference: inputValue(
                                form,
                                'determinationReference'
                              ),
                            }
                          : null,
                      processorContractEndedAt: contractEnded
                        ? peruTime(form.get('processorContractEndedAt'))
                        : null,
                      controllerReturnReference: contractEnded
                        ? inputValue(form, 'controllerReturnReference')
                        : null,
                      statutoryExceptionReference:
                        inputValue(form, 'statutoryExceptionReference') || null,
                      downstreamInstructions: downstream,
                    });
                    await request(`${path}/decision`, caseSchema, {
                      csrf: session.csrf,
                      body,
                    });
                    reload();
                  });
                }}
              >
                <Field label={copy.privacyPurposeEnded}>
                  <input required type="datetime-local" name="purposeEndedAt" />
                </Field>
                <Field label={copy.privacyRetainUntil}>
                  <input required type="datetime-local" name="retainUntil" />
                </Field>
                <Field label={copy.privacyDecisionBasis}>
                  <textarea required maxLength={1000} name="basisReference" />
                </Field>
                <Field label={copy.privacyFinancialClass}>
                  <select
                    required
                    name="financialClassification"
                    defaultValue=""
                    onChange={(event) =>
                      setTax(event.target.value === 'tax_evidence')
                    }
                  >
                    <option value="">{copy.privacySelectClass}</option>
                    {(
                      [
                        'not_financial',
                        'tax_evidence',
                        'other_financial',
                      ] as const
                    ).map((item) => (
                      <option key={item} value={item}>
                        {copy[item]}
                      </option>
                    ))}
                  </select>
                </Field>
                {tax && (
                  <>
                    <Field label={copy.privacyFilingDeadline}>
                      <input required name="filingDeadline" type="date" />
                    </Field>
                    <Field label={copy.privacyPrescriptionEnd}>
                      <input
                        required
                        name="prescriptionEndsAt"
                        type="datetime-local"
                      />
                    </Field>
                    <Field label={copy.privacyTaxDetermination}>
                      <textarea
                        required
                        name="determinationReference"
                        maxLength={1000}
                      />
                    </Field>
                  </>
                )}
                <Field label={copy.privacyContractEnded}>
                  <input
                    type="checkbox"
                    checked={contractEnded}
                    onChange={(event) => setContractEnded(event.target.checked)}
                  />
                </Field>
                {contractEnded && (
                  <>
                    <Field label={copy.privacyContractDate}>
                      <input
                        required
                        name="processorContractEndedAt"
                        type="datetime-local"
                      />
                    </Field>
                    <Field label={copy.privacyControllerReturn}>
                      <textarea
                        required
                        name="controllerReturnReference"
                        maxLength={1000}
                      />
                    </Field>
                  </>
                )}
                <Field label={copy.privacyException}>
                  <textarea
                    name="statutoryExceptionReference"
                    maxLength={1000}
                  />
                </Field>
                <p>{copy.privacyDownstreamHelp}</p>
                {downstream.map((item, index) => (
                  <div key={index}>
                    <Field label={copy.privacyRecipient}>
                      <input
                        required
                        maxLength={1000}
                        value={item.recipient}
                        onChange={(event) =>
                          setDownstream(
                            downstream.map((value, at) =>
                              at === index
                                ? { ...value, recipient: event.target.value }
                                : value
                            )
                          )
                        }
                      />
                    </Field>
                    <Field label={copy.privacyInstruction}>
                      <input
                        required
                        maxLength={1000}
                        value={item.instructionReference}
                        onChange={(event) =>
                          setDownstream(
                            downstream.map((value, at) =>
                              at === index
                                ? {
                                    ...value,
                                    instructionReference: event.target.value,
                                  }
                                : value
                            )
                          )
                        }
                      />
                    </Field>
                    <button
                      type="button"
                      onClick={() =>
                        setDownstream(
                          downstream.filter((_, at) => at !== index)
                        )
                      }
                    >
                      {copy.privacyRemoveRecipient}
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  disabled={downstream.length >= 50}
                  onClick={() =>
                    setDownstream([
                      ...downstream,
                      { recipient: '', instructionReference: '' },
                    ])
                  }
                >
                  {copy.privacyAddRecipient}
                </button>
                <button disabled={mutation.busy}>
                  {copy.privacySaveDecision}
                </button>
              </form>
            </>
          )}
          {['scheduled', 'deleting'].includes(current.data.case.state) && (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void mutation.run(async () => {
                  await request(`${path}/dispose`, disposalSchema, {
                    csrf: session.csrf,
                    body: {},
                  });
                  setPage(null);
                  reload();
                });
              }}
            >
              <p>{copy.privacyDisposalHelp}</p>
              <Field label={copy.privacyConfirmDisposal}>
                <input type="checkbox" required />
              </Field>
              <button disabled={mutation.busy}>
                {current.data.case.state === 'deleting'
                  ? copy.privacyContinueDisposal
                  : copy.privacyDispose}
              </button>
            </form>
          )}
        </>
      )}
    </section>
  );
}
